import validator from 'validator';
import emailMisspelled from 'email-misspelled';
import disposableDomains from 'disposable-email-domains';
import commonEmailProviders from 'email-providers/common.json';

const ERROR_MESSAGES = {
    REQUIRED: 'Email address is required',
    INVALID_FORMAT: 'Please enter a valid email address',
    DISPOSABLE: 'Temporary email addresses are not allowed',
    FAKE: 'Please provide a legitimate email address',
    INVALID_DOMAIN: 'Please enter a valid email domain'
};

/**
 * Email validation utility for frontend form validation
 * Checks syntax, typos, and disposable domains
 */
class EmailValidator {
    constructor() {
        // Use the common email providers list (~360 most popular domains)
        try {
            this.checker = emailMisspelled({
                domains: Array.isArray(commonEmailProviders) ? commonEmailProviders : []
            });
        } catch (error) {
            console.error('[EmailValidator] Failed to initialize email checker:', error);
            this.checker = () => []; // Fallback
        }

        try {
            this.disposableDomains = new Set(Array.isArray(disposableDomains) ? disposableDomains : []);
        } catch (error) {
            console.error('[EmailValidator] Failed to initialize disposable domains:', error);
            this.disposableDomains = new Set();
        }
    }

    /**
     * Comprehensive email validation
     * @param {string} email - Email address to validate
     * @returns {{isValid: boolean, errors: string[], warnings: string[], suggestion: string|null}} Validation result
     */
    validate(email) {
        const result = {
            isValid: true,
            errors: [],
            warnings: [],
            suggestion: null
        };

        // Type check
        if (typeof email !== 'string') {
            result.isValid = false;
            result.errors.push(ERROR_MESSAGES.INVALID_FORMAT);
            return result;
        }

        // Trim and normalize
        email = email.trim().toLowerCase();

        // Check if empty
        if (!email) {
            result.isValid = false;
            result.errors.push(ERROR_MESSAGES.REQUIRED);
            return result;
        }

        // 1. Basic syntax validation using validator.js
        if (!validator.isEmail(email)) {
            result.isValid = false;
            result.errors.push(ERROR_MESSAGES.INVALID_FORMAT);
            return result;
        }

        // Extract domain for further checks
        const domain = email.split('@')[1]?.toLowerCase();

        if (!domain) {
            result.isValid = false;
            result.errors.push(ERROR_MESSAGES.INVALID_FORMAT);
            return result;
        }

        // 2. Check for typos and get suggestions
        try {
            const suggestions = this.checker(email);
            if (suggestions && suggestions.length > 0 && suggestions[0].full) {
                result.suggestion = suggestions[0].full;
                result.warnings.push(`Did you mean ${suggestions[0].full}?`);
            }
        } catch (error) {
            console.error('[EmailValidator] Error checking for typos:', error);
        }

        // 3. Check for disposable/temporary email domains
        if (this.disposableDomains.has(domain)) {
            result.isValid = false;
            result.errors.push(ERROR_MESSAGES.DISPOSABLE);
            return result;
        }

        // 4. Check for obvious fake patterns (combined regex for performance)
        const fakePattern = /^(test|fake|dummy|temp|spam|trash|junk|[a]{4,}|[1]{4,}|abc|123|xyz)@|@(test|fake|example|dummy|temp|invalid)\./i;
        if (fakePattern.test(email)) {
            result.isValid = false;
            result.errors.push(ERROR_MESSAGES.FAKE);
            return result;
        }

        return result;
    }

    /**
     * Quick validation that returns boolean only
     * @param {string} email - Email address to validate
     * @returns {boolean}
     */
    isValid(email) {
        return this.validate(email).isValid;
    }
}

// Create singleton instance
const emailValidator = new EmailValidator();

/**
 * Initialize email validation for form inputs
 * Add data-email-validate attribute to email inputs to enable validation
 */
export function initEmailValidation() {
    // Guard against duplicate initialization
    if (document.body.hasAttribute('data-email-validation-initialized')) {
        console.warn('[EmailValidator] Already initialized');
        return;
    }
    document.body.setAttribute('data-email-validation-initialized', 'true');

    const emailInputs = document.querySelectorAll('input[type="email"][data-email-validate]');
    const validatedForms = new WeakSet();

    emailInputs.forEach((input) => {
        const form = input.closest('form');
        const errorContainer = createOrGetErrorContainer(input);
        const suggestionContainer = createOrGetSuggestionContainer(input);

        // Real-time validation on blur
        input.addEventListener('blur', () => {
            validateInput(input, errorContainer, suggestionContainer);
        });

        // Clear validation on input
        input.addEventListener('input', () => {
            clearValidation(input, errorContainer, suggestionContainer);
        });

        // Validate on form submit - only add listener once per form
        if (form && !validatedForms.has(form)) {
            validatedForms.add(form);
            form.addEventListener('submit', (e) => {
                const formEmailInputs = form.querySelectorAll('input[type="email"][data-email-validate]');
                let allValid = true;
                let firstInvalidInput = null;

                formEmailInputs.forEach((emailInput) => {
                    // Skip empty non-required fields
                    if (!emailInput.required && !emailInput.value.trim()) {
                        return;
                    }

                    const emailErrorContainer = createOrGetErrorContainer(emailInput);
                    const emailSuggestionContainer = createOrGetSuggestionContainer(emailInput);
                    const isValid = validateInput(emailInput, emailErrorContainer, emailSuggestionContainer);

                    if (!isValid) {
                        allValid = false;
                        if (!firstInvalidInput) {
                            firstInvalidInput = emailInput;
                        }
                    }
                });

                if (!allValid) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();

                    // Focus first invalid input
                    if (firstInvalidInput) {
                        firstInvalidInput.focus();
                        firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }
            }, true); // Use capture phase to run before other handlers
        }
    });
}

/**
 * Validate a single input field
 * @param {HTMLInputElement} input
 * @param {HTMLElement} errorContainer
 * @param {HTMLElement} suggestionContainer
 * @returns {boolean}
 */
function validateInput(input, errorContainer, suggestionContainer) {
    const email = input.value;

    // Clear previous validation state
    clearValidation(input, errorContainer, suggestionContainer);

    // Validate email
    const result = emailValidator.validate(email);

    if (!result.isValid) {
        input.classList.add('error');
        input.setAttribute('aria-invalid', 'true');

        // Make input invalid for native HTML5 validation
        input.setCustomValidity(result.errors[0] || 'Invalid email');

        if (result.errors.length > 0) {
            errorContainer.textContent = result.errors[0];
            errorContainer.classList.remove('hidden');
        }

        return false;
    }

    // Clear custom validity
    input.setCustomValidity('');

    // Show suggestion if available
    if (result.suggestion) {
        showSuggestion(input, errorContainer, suggestionContainer, result.suggestion);
    }

    input.classList.add('valid');
    input.setAttribute('aria-invalid', 'false');
    return true;
}

/**
 * Clear validation state
 * @param {HTMLInputElement} input
 * @param {HTMLElement} errorContainer
 * @param {HTMLElement} suggestionContainer
 */
function clearValidation(input, errorContainer, suggestionContainer) {
    input.classList.remove('error', 'valid');
    input.removeAttribute('aria-invalid');
    input.setCustomValidity(''); // Clear custom validity
    errorContainer.classList.add('hidden');
    errorContainer.textContent = '';
    suggestionContainer.classList.add('hidden');
    suggestionContainer.innerHTML = '';
}

/**
 * Show typo suggestion (XSS-safe using DOM methods)
 * @param {HTMLInputElement} input
 * @param {HTMLElement} errorContainer
 * @param {HTMLElement} suggestionContainer
 * @param {string} suggestion
 */
function showSuggestion(input, errorContainer, suggestionContainer, suggestion) {
    // Clear previous content
    suggestionContainer.innerHTML = '';

    // Create elements safely to prevent XSS
    const span = document.createElement('span');
    span.textContent = 'Did you mean ';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'email-suggestion-btn underline';
    btn.dataset.suggestion = suggestion;
    btn.textContent = suggestion;

    // Use { once: true } to prevent memory leaks
    btn.addEventListener('click', () => {
        input.value = suggestion;
        clearValidation(input, errorContainer, suggestionContainer);
        input.classList.add('valid');
    }, { once: true });

    span.appendChild(btn);
    span.appendChild(document.createTextNode('?'));

    suggestionContainer.appendChild(span);
    suggestionContainer.classList.remove('hidden');
}

/**
 * Create or get error container for input
 * @param {HTMLInputElement} input
 * @returns {HTMLElement}
 */
function createOrGetErrorContainer(input) {
    let container = input.parentElement.querySelector('.email-error');

    if (!container) {
        container = document.createElement('div');
        container.className = 'email-error text-red-600 text-sm mt-1 hidden';
        container.setAttribute('role', 'alert');
        container.setAttribute('aria-live', 'polite');
        container.setAttribute('aria-atomic', 'true');
        input.parentElement.appendChild(container);
    }

    return container;
}

/**
 * Create or get suggestion container for input
 * @param {HTMLInputElement} input
 * @returns {HTMLElement}
 */
function createOrGetSuggestionContainer(input) {
    let container = input.parentElement.querySelector('.email-suggestion');

    if (!container) {
        container = document.createElement('div');
        container.className = 'email-suggestion text-blue-600 text-sm mt-1 hidden';
        input.parentElement.appendChild(container);
    }

    return container;
}

// Export validator instance and initialization function
export { emailValidator, initEmailValidation as default };
